import { EMPTY, of, concat, from, merge } from 'rxjs';
import { mergeMap, throttleTime, catchError, take, filter } from 'rxjs/operators';
import { ofType } from 'redux-observable';

import get from 'lodash.get';
import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import { ModalService } from 'ui-components/services/modal-service';
import {
  NotificationService,
  NOTIFICATION_TYPE_ERROR,
  NOTIFICATION_DURATIONS,
  NOTIFICATION_TYPE_SUCCESS,
} from 'ui-components/services/notification-service';

import Global from '../../store/global-selectors';
import * as SelectedOrdersActions from '../../store/orders/selected-orders/orders.selected-orders.actions';
import * as OrderActions from '../../store/orders/orders.actions';
import * as ShipmentActions from '../../store/shipments/shipments.actions';
import * as OrderGridActions from '../../store/orders/grid/orders.grid.actions';
import * as ShipmentGridActions from '../../store/shipments/grid/shipments.grid.actions';
import * as OrderDetailsActions from '../../store/orders/order-details/orders.details.actions';
import * as BulkActions from '../../store/bulk/bulk.actions';
import * as OrdersDrawerActions from '../../store/orders/drawer/orders.drawer.actions';
import * as Actions from '../../store/mark-as-shipped/mark-as-shipped.actions';
import { FulfillmentPlanOrderGridStatus } from '../../enums/fulfillmentplan-order-grid-status';
import { updateFulfillmentPlanOrderGridStatus } from '../../fulfillment-plans/actions';
import * as MarkAsShippedApi from '../../store/mark-as-shipped/mark-as-shipped.api';
import { linkIDs } from '../../services/link-service/links';
import HelpArticleLink from '../../components/help-article-link';
import { labelBatchListener } from '../../services/pusher-service/persistent-listeners';

import { ErrorModal } from '../../components/modals/error-modal';
import { getUserFriendlyMessagesForErrorResponse } from '../../errors/user-friendly-error-message-service';

const messages = defineMessages({
  title: {
    description: 'markShippedErrorModal.title',
    defaultMessage: 'Mark As Shipped Failed',
    id: 'jLPHjf',
  },
  body: {
    description: 'markShippedErrorModal.message',
    defaultMessage: 'Something went wrong with your request.',
    id: 'ZTxtA8',
  },
  ok: {
    description: 'markShippedErrorModal.ok',
    defaultMessage: 'Ok',
    id: 'S12PHl',
  },
  hasAlreadyShippedTop: {
    description: 'markShippedErrorModal error for already shipped fulfillment top section',
    defaultMessage: 'To correct previous tracking numbers, click <a>Undo Mark as Shipped</a> and try again.',
    id: '8dcdDK',
  },
  hasAlreadyShippedBottom: {
    description: 'markShippedErrorModal error for already shipped fulfillment bottom section',
    defaultMessage: 'Or, to ship this order again, use the create another shipment option instead.',
    id: '7nBnNI',
  },
  undoMarkAsShippedErrorTitle: {
    description: 'error title indicating that the "undo mark as shipped" workflow has failed',
    defaultMessage: "'Undo Mark as Shipped' Failed",
    id: 'oW4HpL',
  },
  undoMarkAsShippedErrorMessage: {
    description: 'error message indicating that the "undo mark as shipped" workflow has failed',
    defaultMessage:
      "Failed to process your 'Undo Mark as Shipped' request. Please try again later and contact support if the issue persists.",
    id: 'rHPwEq',
  },
  markedAsShippedSuccessTitle: {
    description: 'Title for the success message when orders are marked as shipped',
    defaultMessage: 'Orders Marked as Shipped',
    id: 'YWSbxv',
  },
  markedAsShippedSuccessBody: {
    description: 'Body text for the success message when orders are marked as shipped',
    defaultMessage: 'Successfully Marked Orders as Shipped',
    id: 'QOXupK',
  },
});

const showUndoMarkAsShippedError = () => {
  NotificationService.showNotification({
    notificationType: NOTIFICATION_TYPE_ERROR,
    title: <FormattedMessage {...messages.undoMarkAsShippedErrorTitle} />,
    content: <FormattedMessage {...messages.undoMarkAsShippedErrorMessage} />,
    duration: NOTIFICATION_DURATIONS.SHORT,
  });
};

const contentForAlreadyShippedErrorModal = () => {
  return (
    <>
      <FormattedMessage
        {...messages.hasAlreadyShippedTop}
        values={{
          a: (chunks) => <HelpArticleLink articleId={linkIDs.MarkOrdersShipped}>{chunks}</HelpArticleLink>,
        }}
      />
      <br />
      <br />
      <FormattedMessage {...messages.hasAlreadyShippedBottom} />
    </>
  );
};

export const markAsShippedEpic = (action$) =>
  action$.pipe(
    ofType(Actions.MARK_SHIPPED_REQUEST),
    mergeMap(({ payload }) =>
      MarkAsShippedApi.markAsShipped(payload).pipe(
        mergeMap((response) => {
          const { onStart, onFinish } = labelBatchListener.createSingleJobScopedHandlers();
          onStart(response.batchIds, {});
          const fulfillmentPlanIds = ((payload || {}).fulfillmentPlans || []).map((fp) => fp.fulfillmentPlanId);
          if (response.fulfillmentPlans?.some((plan) => plan.hasShippedFulfillment)) {
            ModalService.launchModal({
              component: ErrorModal,
              props: {
                state: 'warning',
                title: <FormattedMessage {...messages.title} />,
                message: contentForAlreadyShippedErrorModal(),
                buttons: [
                  {
                    theme: 'primary',
                    children: <FormattedMessage {...messages.ok} />,
                  },
                ],
              },
            });
            return EMPTY;
          }
          return merge(
            of(
              SelectedOrdersActions.setSelectedOrdersPreCheck([]),
              OrderGridActions.showTheSpinnerOnOrderGrid(),
              Actions.markShippedSuccess(response, fulfillmentPlanIds),
            ),
            action$.pipe(
              ofType(Actions.MARK_SHIPPED_PUSHER_MESSAGE),
              filter((message) => {
                return response.batchIds.includes(message.batchId);
              }),
              take(1),
              mergeMap(() => {
                onFinish(response.batchIds);
                return EMPTY;
              }),
            ),
          );
        }),
        catchError((error) => {
          if (error?.response) {
            return of(Actions.markShippedFailure(error.response.data));
          }
          return EMPTY;
        }),
      ),
    ),
  );

export const refreshGridAfterSuccessfullyMarkingOrdersOrShipmentsAsShipped = (action$, { getState }) =>
  action$.pipe(
    ofType(Actions.MARK_SHIPPED_PUSHER_MESSAGE, BulkActions.BULK_PROCESSING_CREATE_LABEL_FINAL_MESSAGE),
    throttleTime(1000),
    mergeMap((action) => {
      const state = getState();
      const batchId = get(action, 'batchId');
      const orderBatches = Global.selectMarkAsShippedOrderBatchIds(state);
      const shipmentBatches = Global.selectMarkAsShippedShipmentBatchIds(state);
      const orderDetailsSalesOrderId = Global.selectOrderDetailsSalesOrderId(state);
      const sidebarIsVisible = Global.selectOrdersGridisSidebarVisible(state);
      const successInfo = {
        title: <FormattedMessage {...messages.markedAsShippedSuccessTitle} />,
        content: <FormattedMessage {...messages.markedAsShippedSuccessBody} />,
        notificationType: NOTIFICATION_TYPE_SUCCESS,
        duration: 3000,
      };

      if (orderBatches.includes(batchId)) {
        NotificationService.showNotification({ ...successInfo });
        if (orderDetailsSalesOrderId) {
          const actions$ = [
            of(OrderActions.silentRequestGetOrders()),
            of(OrderDetailsActions.requestOrderDetails()),
            of(Actions.clearBatchFromOrders(batchId)),
          ];

          if (sidebarIsVisible) {
            actions$.push(of(OrdersDrawerActions.getDrawerOrders()));
          }
          return concat(...actions$);
        }

        return concat(of(OrderActions.silentRequestGetOrders()), of(Actions.clearBatchFromOrders(batchId)));
      }
      if (shipmentBatches.includes(batchId)) {
        NotificationService.showNotification({ ...successInfo });
        return concat(of(ShipmentActions.silentRequestGetShipments()), of(Actions.clearBatchFromShipments(batchId)));
      }
      return EMPTY;
    }),
  );

export const showThatAnErrorHappened = (action$) =>
  action$.pipe(
    ofType(Actions.MARK_SHIPPED_FAILURE),
    mergeMap(({ error }) => {
      const { errors } = getUserFriendlyMessagesForErrorResponse(error);
      const subscriptionError = errors[0];
      const message = subscriptionError?.messageDescriptor || messages.body;
      const messageValues = subscriptionError?.values;

      ModalService.launchModal({
        component: ErrorModal,
        props: {
          state: 'warning',
          title: <FormattedMessage {...messages.title} />,
          message: <FormattedMessage {...message} values={messageValues} />,
          buttons: [
            {
              theme: 'primary',
              children: <FormattedMessage {...messages.ok} />,
            },
          ],
        },
      });

      return EMPTY;
    }),
  );

export const undoMarkAsShippedEpic = (action$) =>
  action$.pipe(
    ofType(Actions.UNDO_MARK_SHIPPED_REQUEST),
    mergeMap(({ fulfillmentPlanIds }) =>
      MarkAsShippedApi.undoMarkAsShipped(fulfillmentPlanIds).pipe(
        mergeMap((response) =>
          concat(of(Actions.undoMarkShippedSuccess(response, fulfillmentPlanIds)), of(ShipmentGridActions.showTheSpinnerOnShipmentsGrid())),
        ),
        catchError(() => {
          showUndoMarkAsShippedError();
          return EMPTY;
        }),
      ),
    ),
  );

export const undoMarkAsShippedFromOrderDetails = (action$) =>
  action$.pipe(
    ofType(Actions.UNDO_MARK_AS_SHIPPED_FROM_ORDER_DETAILS),
    mergeMap(({ fulfillmentId }) => {
      return from(MarkAsShippedApi.undoMarkAsShippedFromOrderDetails(fulfillmentId)).pipe(
        mergeMap(() => of(Actions.undoMarkShippedSuccess())),
        catchError(() => {
          showUndoMarkAsShippedError();
          return EMPTY;
        }),
      );
    }),
  );

export const undoMarkAsShippedSuccessEpic = (action$) =>
  action$.pipe(
    ofType(Actions.UNDO_MARK_SHIPPED_SUCCESS),
    mergeMap(({ fulfillmentPlanIds }) => {
      if (!fulfillmentPlanIds) {
        return concat(of(ShipmentActions.silentRequestGetShipments()), of(Actions.shipmentDetailsUndoMarkShipped()));
      }

      const [fulfillmentPlanId] = fulfillmentPlanIds;
      return concat(
        of(
          updateFulfillmentPlanOrderGridStatus({
            fulfillmentPlanId,
            orderGridStatus: FulfillmentPlanOrderGridStatus.AwaitingShipment.name,
          }),
        ),
        of(ShipmentActions.silentRequestGetShipments()),
        of(Actions.shipmentDetailsUndoMarkShipped()),
      );
    }),
  );
